/**
 * Portal-side currency formatting.
 *
 * The shared kernel owns ISO capability and the Hub-facing helpers. What lives
 * here is the portal's own axis: the reader's chosen language, which decides
 * the separators *and* which side of the number the symbol sits on. The Hub has
 * no language switcher, so it has no equivalent.
 */

import { toBcp47 } from '@ophelos/shared/utils/languages';
import { currencySymbol as kernelSymbol, normalizeCurrency } from '@ophelos/shared/utils/currency';

export { DEFAULT_CURRENCY, formatMoneyCompact } from '@ophelos/shared/utils/currency';

const symbols = new Map<string, string>();

/**
 * The kernel builds a whole formatter per call (~15µs, measured — and it
 * dominated `formatCurrencyParts`). A symbol is a pure function of the
 * currency, so it is cached here: at most one entry per supported code.
 */
export function currencySymbol(currency: string): string {
  const code = normalizeCurrency(currency);
  const cached = symbols.get(code);
  if (cached) return cached;

  const made = kernelSymbol(code);
  symbols.set(code, made);
  return made;
}

/** The locales the portal formats in — one per supported language. */
export type PortalLocale = string;

/**
 * Resolve a language code to the locale the portal formats in. Short codes are
 * accepted because several `useTranslation` test mocks return one, and bare
 * `pt` means Brazil to `Intl` — which leads with the euro sign where pt-PT
 * trails it.
 */
export const getLocaleFromLanguage = (languageCode: string): PortalLocale => toBcp47(languageCode);

/**
 * Every formatter below comes from here. Constructing an `Intl.NumberFormat`
 * costs ~15µs against ~0.3µs to reuse one, and these run inside render.
 * Reproduce with 200k iterations of
 * `new Intl.NumberFormat(l, o).format(v)` against one hoisted instance,
 * discarding warm-up and taking the median.
 */
const formatters = new Map<string, Intl.NumberFormat>();

function formatter(locale: string, currency: string): Intl.NumberFormat {
  const key = `${locale}|${currency}`;
  const cached = formatters.get(key);
  if (cached) return cached;

  const made = new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
  formatters.set(key, made);
  return made;
}

/** Convert major currency units to subunits. */
export const toSubunits = (amount: number): number => Math.round(amount * 100);

/** Convert subunits to major currency units. */
export const fromSubunits = (subunits: number): number => subunits / 100;

/** Convert ledger millicents to major currency units — €1.00 is 100,000 millicents (see `precision.py`). */
export const fromMillicents = (millicents: number): number => millicents / 100_000;

/**
 * Money as a plain string for interpolating into translated copy: formatted in
 * the reader's language, denominated in the account's currency. `Intl` places
 * the symbol, so it can never live in the copy itself.
 *
 * Use `CurrencyDisplay` where styled markup is wanted; it resolves the locale
 * from the same place and puts the symbol on the same side, so the two agree on
 * digits, separators and placement. It spaces its spans with a CSS gap rather
 * than the non-breaking space `Intl` emits, so they are not byte-identical.
 */
export const formatMoneyInLanguage = (amount: number, language: string, currency: string): string =>
  formatter(getLocaleFromLanguage(language), normalizeCurrency(currency)).format(amount);

/**
 * Split format for styled displays: the pieces needed to size cents apart from
 * the integer, plus which side the symbol belongs on.
 *
 * `Intl` owns grouping, the decimal separator and the symbol's position, so
 * there is no separator table to keep in step with the supported-currency list.
 * Fraction digits are fixed at two — true of every code in
 * `SUPPORTED_CURRENCIES`, and the split `cents` field assumes it.
 */
export function formatCurrencyParts(
  amount: number,
  locale: string,
  currency: string
): { integer: string; cents: string; symbol: string; decimalSeparator: string; symbolFirst: boolean } {
  const parts = formatter(locale, normalizeCurrency(currency)).formatToParts(Math.abs(amount));
  const join = (type: Intl.NumberFormatPartTypes) => parts.filter((p) => p.type === type).map((p) => p.value).join('');
  const integer = parts.filter((p) => p.type === 'integer' || p.type === 'group').map((p) => p.value).join('');

  return {
    integer: amount < 0 ? `-${integer}` : integer,
    cents: join('fraction'),
    // Sourced from the currency's own locale, not the display one, so USD stays
    // a bare '$' rather than the 'US$' Intl gives it inside en-GB.
    symbol: currencySymbol(currency),
    decimalSeparator: join('decimal'),
    symbolFirst: parts.findIndex((p) => p.type === 'currency') < parts.findIndex((p) => p.type === 'integer'),
  };
}
